import { Model } from "./Model";
import { v3 } from "oc-threejs/short3";
import * as Maths from "oc-commons/Maths";
import * as Maths3 from "oc-threejs/Maths3";
import * as Commons from "oc-commons/Commons";

import { Jaco } from "./Jaco";
import { Box3, BoxGeometry, Vector3, BufferGeometry } from "three";

export interface TModelCuttedClippingParameters {
  size: Vector3;
  fixGridPosition: Vector3;
}
export function createDefaultClippingParamters(): TModelCuttedClippingParameters {
  return {
    size: v3(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY),
    fixGridPosition: v3(0, 0, 0),
  };
}

/**
 * Schneidet ein Model auf eine bestimmte Größe zu (also eine Box). Das "Werkstück" wird dabei bei der fixGridPosition eingespannt und
 * zu der oder den anderen Seiten hin abgeschnitten.
 * Bei fixGridPosition (0,0,0) also bei box.min eingespannt und zur max - Seite hin abgeschnitten.
 */
export class ModelSizeCutted extends Model {
  // hier kommen die Vorgaben rein, die in  clippingParameters kopiert werden (achtung, nicht referenz gleich setzen!)
  defaultClippingParameters: TModelCuttedClippingParameters = createDefaultClippingParamters();
  // clippingParameters werden aufgrund der generischen Natur nicht automatisch persistiert - hierzu Model ableiten
  clippingParameters = Commons.cloneDeep(this.defaultClippingParameters);

  constructor(parent: Jaco | null, modelMaterialRelation: any) {
    super(parent, modelMaterialRelation);
  }

  isClipping(): boolean {
    if (!super.isClipping()) {
      return false;
    }
    const epsilon = 0.0005;
    const fixGP = this.clippingParameters.fixGridPosition;
    // Maths3.box.one is likely a Box3 or similar.
    const fixGPContained = Maths3.box.one.distanceToPoint(fixGP) < epsilon;
    if (!fixGPContained) {
      return true;
    }
    const sizeUncutted = this.getBoxUnclipped().getSize(v3());
    const sizeCutted = this.getSizeCutted(0);
    const someBigger = ["x", "y", "z"].some(
      (axis) => (sizeUncutted as any)[axis] > (sizeCutted as any)[axis],
    );
    return someBigger && sizeUncutted.distanceTo(sizeCutted) > epsilon;
  }

  getClippingGeometry(): BufferGeometry | null | undefined {
    // 1. Größe nach Zuschnitt ermitteln
    // 2. Grid-Punkt des Zuschnitts auf Grid-Punkt des Originals verschieben

    // die nicht zugeschnittene lokale Bounding Box
    const uncuttedBox = this.getBoxUnclipped();
    // die zugeschnittene lokale Bounding Box
    const sizeCutted = this.getSizeCutted(0.0001);

    // die zugeschnittene lokale Box Geometry ist erstmal immer zentriert im Ursprung
    const cuttedGeometry = new BoxGeometry(sizeCutted.x, sizeCutted.y, sizeCutted.z);
    const cuttedBox = new Box3().setFromCenterAndSize(v3(), sizeCutted);

    // Die fixierte GridPosition, an der das Werkstück eingespannt ist, d.h.
    // an den gegenüberliegenden Enden wird zugeschnitten
    const fixGP = this.clippingParameters.fixGridPosition;
    // Mache die Fixpunkte gleich
    const fixUncutted = (uncuttedBox as any).grid(fixGP); // Assume grid extension method
    const fixCutted = (cuttedBox as any).grid(fixGP);
    const t = fixUncutted.clone().sub(fixCutted);

    cuttedGeometry.translate(t.x, t.y, t.z);
    cuttedGeometry.computeBoundingBox();

    // BoxBufferGeometry erzeugt pro Material einen MaterialIndex. Damit die sich nicht bei der
    // Intersection übertragen, wird hiermit der MaterialIndex überall auf 0 gesetzt:
    cuttedGeometry.groups = [];

    return cuttedGeometry;
  }

  /** */
  getSizeCutted(epsilon = 0) {
    const uncuttedBox = this.getBoxUnclipped();
    const sizeUncutted = uncuttedBox.getSize(v3()).toArray();
    const sizeCutted = v3().fromArray(
      this.clippingParameters.size
        .toArray()
        .map((c: number, i: number) => (Maths.notFinite(c) ? sizeUncutted[i] + epsilon : c)),
    );
    return sizeCutted;
  }
}
